import { c as _c } from "react/compiler-runtime";
import { StarRating, StarRatingProps } from '@udemy-v2/react-merchandising-components';
import { useI18n } from '@udemy-v2/i18n';
import { TrackImpression } from '@udemy-v2/event-tracking';
import { Tooltip } from '@udemy-v2/react-popup-components';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { ReviewSummaryRatingGauge } from './review-summary-rating-gauge.react-component';
import styles from './review-summary-widget.module.scss';
const noop = () => {};
interface ReviewSummaryWidgetProps {
  isFreeSEOExp?: boolean;
  showAverageRating?: boolean;
  averageRating: number;
  totalDistributionCount: number;
  ratingDistribution: {
    rating: number;
    count: number;
  }[];
  selectedRating?: number;
  onRatingSelected?: (rating: number | undefined) => void;
  onSeen?: () => void;
  ratingText?: string;
  ratingSize?: StarRatingProps['size'];
  averageContainerProps?: React.HTMLAttributes<HTMLDivElement>;
}
export const ReviewSummaryWidget = t0 => {
  const $ = _c(50);
  const {
    isFreeSEOExp: t1,
    showAverageRating: t2,
    averageRating,
    totalDistributionCount,
    ratingDistribution,
    selectedRating,
    onRatingSelected,
    onSeen: t3,
    ratingText,
    ratingSize: t4,
    averageContainerProps: t5
  } = t0;
  const isFreeSEOExp = t1 === undefined ? false : t1;
  const showAverageRating = t2 === undefined ? false : t2;
  const onSeen = t3 === undefined ? noop : t3;
  const ratingSize = t4 === undefined ? "large" : t4;
  let t6;
  if ($[0] !== t5) {
    t6 = t5 === undefined ? {} : t5;
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const averageContainerProps = t6;
  const {
    gettext,
    interpolate
  } = useI18n();
  let distribution;
  if ($[2] !== ratingDistribution || $[3] !== totalDistributionCount) {
    let t7;
    if ($[5] !== totalDistributionCount) {
      t7 = range => ({
        ...range,
        percentage: totalDistributionCount > 0 ? range.count / totalDistributionCount * 100 : 0
      });
      $[5] = totalDistributionCount;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    distribution = ratingDistribution.map(t7);
    const totalPercentage = distribution.reduce(_temp, 0);
    if (totalPercentage !== 100 && distribution.length > 0) {
      let highestRangeIndex = 0;
      let highestPercentage = 0;
      distribution.forEach((range_1, index) => {
        if (range_1.percentage > highestPercentage) {
          highestPercentage = range_1.percentage;
          highestRangeIndex = index;
        }
      });
      distribution[highestRangeIndex].percentage = distribution[highestRangeIndex].percentage + (100 - totalPercentage);
    }
    $[2] = ratingDistribution;
    $[3] = totalDistributionCount;
    $[4] = distribution;
  } else {
    distribution = $[4];
  }
  const ratingDistributionWithPercentage = distribution;
  let t7;
  if ($[7] !== gettext || $[8] !== interpolate) {
    t7 = rating => interpolate(gettext("Rating: %(rating)s out of 5"), {
      rating
    }, true);
    $[7] = gettext;
    $[8] = interpolate;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const getAriaTextForRating = t7;
  if (averageRating === 0) {
    let t8;
    if ($[10] !== gettext) {
      t8 = gettext("This course doesn't have any reviews yet.");
      $[10] = gettext;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== t8) {
      t9 = <div data-purpose="no-ratings-message">{t8}</div>;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  const renderFilterApplied = _temp2;
  let t8;
  if ($[14] !== selectedRating) {
    t8 = <div role="status" className="ud-sr-only">{renderFilterApplied(selectedRating)}</div>;
    $[14] = selectedRating;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== averageContainerProps || $[17] !== averageRating || $[18] !== getAriaTextForRating || $[19] !== gettext || $[20] !== isFreeSEOExp || $[21] !== ratingSize || $[22] !== ratingText || $[23] !== showAverageRating) {
    t9 = showAverageRating && <div {...averageContainerProps} className={classNames("ud-text-md", styles["average-container"], averageContainerProps.className)}><div data-purpose="average-rating" className={classNames("ud-heading-xxl", styles["average-number"])}>{averageRating.toFixed(1)}</div><div className={styles["average-stars"]}><StarRating data-purpose="star-rating-average" ariaLabel={getAriaTextForRating(averageRating)} rating={averageRating} size={ratingSize} /></div><div className={classNames("ud-heading-lg", styles["average-rating-text"])}><Tooltip placement="bottom" renderTrigger={triggerProps => <span {...triggerProps}>{ratingText ?? (isFreeSEOExp ? gettext("Tutorial rating") : gettext("Course Rating"))}</span>}>{gettext("Course Ratings are calculated from individual students' ratings \n                                            and a variety of other signals, like age of rating and reliability, to ensure \n                                            that they reflect course quality fairly and accurately.")}</Tooltip></div></div>;
    $[16] = averageContainerProps;
    $[17] = averageRating;
    $[18] = getAriaTextForRating;
    $[19] = gettext;
    $[20] = isFreeSEOExp;
    $[21] = ratingSize;
    $[22] = ratingText;
    $[23] = showAverageRating;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== showAverageRating) {
    t10 = classNames(styles.rates, {
      [styles["with-average-rating"]]: showAverageRating
    });
    $[25] = showAverageRating;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== getAriaTextForRating || $[28] !== onRatingSelected || $[29] !== ratingDistributionWithPercentage || $[30] !== ratingSize || $[31] !== selectedRating) {
    let t12;
    if ($[33] !== getAriaTextForRating || $[34] !== onRatingSelected || $[35] !== ratingSize || $[36] !== selectedRating) {
      t12 = (range_2, index_0) => <ReviewSummaryRatingGauge key={`rating-${range_2.rating}-${index_0}`} ariaLabel={getAriaTextForRating(range_2.rating)} selectedRating={selectedRating} rating={range_2.rating} percent={range_2.percentage} size={ratingSize} onRatingSelected={onRatingSelected} />;
      $[33] = getAriaTextForRating;
      $[34] = onRatingSelected;
      $[35] = ratingSize;
      $[36] = selectedRating;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    t11 = ratingDistributionWithPercentage.map(t12).reverse();
    $[27] = getAriaTextForRating;
    $[28] = onRatingSelected;
    $[29] = ratingDistributionWithPercentage;
    $[30] = ratingSize;
    $[31] = selectedRating;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[38] !== t10 || $[39] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t9) {
    t13 = <div className={styles["stats-container"]}>{t9}{t12}</div>;
    $[41] = t12;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t13 || $[45] !== t8) {
    t14 = <div data-purpose="ratings-and-reviews">{t8}{t13}</div>;
    $[44] = t13;
    $[45] = t8;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== onSeen || $[48] !== t14) {
    t15 = <TrackImpression trackFunc={onSeen}>{t14}</TrackImpression>;
    $[47] = onSeen;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
};
function _temp(total, range_0) {
  return total + Math.round(range_0.percentage);
}
function _temp2(filter) {
  const filterApplied = `${filter} star filter applied.`;
  return <span>{filter ? filterApplied : "No filter applied"}</span>;
}